import { token } from '@/infrastructure/container';
import type { IAppBus } from './AppBus';
import type { ILogger } from './Logger';

// `navigator.onLine` plus the online/offline events, wrapped so a store can gate on connectivity
// without touching a platform global (INVARIANTS rule 6's platform ban, rule 12's recipe).
//
// grading-day is online-only by design: a vote, a registration, a tick all need the gateway, and
// a coach whose phone dropped off the hall wifi has to see that rather than tap into a void. This
// service is the single source of that fact.
//
// `navigator.onLine` is a weak signal (it means "the OS has a network interface", not "the
// gateway answers"), so treat it as the fast negative: false is reliably offline, true is only a
// hint. A failed write is still the authority on whether a write landed.

export interface IDomConnectivity {
  start(): void;
  get online(): boolean;
}

export const TOKEN_IDomConnectivity = token<IDomConnectivity>('IDomConnectivity');

export class DomConnectivity implements IDomConnectivity {
  private started = false;

  constructor(
    private readonly bus: IAppBus,
    private readonly logger: ILogger,
  ) {}

  // Idempotent, and deliberately not called from the constructor: a service that starts listening
  // when it is built cannot be resolved in a test without side effects. Called once at boot,
  // after the subscribers are live.
  start(): void {
    if (this.started) return;
    this.started = true;
    window.addEventListener('online', this.onChange);
    window.addEventListener('offline', this.onChange);
    this.logger.info('DomConnectivity started', this.online);
    // Publish the initial state: a subscriber that only ever hears about changes never learns it
    // booted offline.
    this.bus.connectivityChanged(this.online);
  }

  get online(): boolean {
    return navigator.onLine;
  }

  private onChange = (): void => {
    this.logger.debug('connectivity', this.online);
    this.bus.connectivityChanged(this.online);
  };
}
